import { routeCatalog } from "./catalog";
import type { RouteAssetRef } from "./types";

export type QuestAssetKind = RouteAssetRef["kind"];

export type QuestAssetSourceType =
  | "generated"
  | "generated_placeholder"
  | "public_domain_reference"
  | "composite";

export type QuestAssetReviewStatus =
  | "planned"
  | "generated"
  | "reviewed"
  | "accepted"
  | "rejected";

export interface QuestAssetManifestEntry {
  assetId: string;
  routeId: string;
  kind: QuestAssetKind;
  path: string;
  optimizedPath: string;
  contentHash: string | null;
  sourceType: QuestAssetSourceType;
  sourceUrl: string | null;
  publicDomainNote: string;
  attributionRequired: boolean;
  prompt: string;
  generationCommand: string;
  reviewStatus: QuestAssetReviewStatus;
  blockedMarks: string[];
}

const generatedAssetOverrides: Record<
  string,
  Partial<
    Pick<
      QuestAssetManifestEntry,
      | "contentHash"
      | "sourceType"
      | "sourceUrl"
      | "publicDomainNote"
      | "attributionRequired"
      | "prompt"
      | "generationCommand"
      | "reviewStatus"
      | "blockedMarks"
    >
  >
> = {
  "moon-trek-hero": {
    contentHash:
      "e632bffbc0007e2597ec1769677c56354f49a8e7534429dc04a8a31f398ce6ab",
    sourceType: "generated",
    prompt:
      "Moon Trek route hero image for Fitness RPG, grand lunar nearside expedition from Mare Tranquillitatis toward Tycho Crater, astronaut-free lunar landscape with silver regolith, distant crater rims, Earth low in black sky, teal-aqua accent light, cinematic optimistic RPG adventure dashboard art, no text, no logos, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "moon-trek-sight-mare-tranquillitatis": {
    contentHash:
      "be495c80ffcd2c30852360df6ed627a4fe64ae67a02d699800a440d16f072575",
    sourceType: "generated",
    prompt:
      "Mare Tranquillitatis sight card for Fitness RPG Moon Trek, lunar plain inspired by Apollo-era terrain, Earth visible above horizon, gentle teal UI accent glow, cinematic game scene, readable at mobile size, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/sight-mare-tranquillitatis.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "moon-trek-map-lroc-color-2k": {
    contentHash:
      "f7130a1822681fa7512d7dcfd40db8c10b9ba4f06777910348698260ed7a2170",
    sourceType: "public_domain_reference",
    sourceUrl: "https://svs.gsfc.nasa.gov/4720",
    publicDomainNote:
      "NASA Scientific Visualization Studio CGI Moon Kit LROC color map assembled from Lunar Reconnaissance Orbiter data. NASA visualizations are public-domain unless noted; credit NASA's Scientific Visualization Studio.",
    attributionRequired: false,
    prompt:
      "Fitness RPG Moon Trek public-domain lunar map asset from NASA SVS CGI Moon Kit LROC color map, used as route backdrop with waypoint overlays.",
    generationCommand:
      "curl -L https://svs.gsfc.nasa.gov/vis/a000000/a004700/a004720/lroc_color_2k.jpg -o public/assets/quests/moon-trek/map-lroc-color-2k.jpg && node scripts/optimize-quest-assets.mjs",
    reviewStatus: "accepted",
  },
  "moon-trek-badge-complete": {
    contentHash:
      "96ba94e3607eb110cf77b202bb91a997580dde71b356123cfee26aafa7bdf8ba",
    sourceType: "generated",
    prompt:
      "Moon Trek completion badge icon for a fantasy fitness RPG, circular lunar medal with Tycho crater rays and small teal moonstone gem, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "moon-trek-item-regolith-compass": {
    contentHash:
      "b5d2cd82974b7f3be3408db79ed5be5d329fe174ab2904b492295e37c2a046eb",
    sourceType: "generated",
    prompt:
      "Regolith Compass item icon for Fitness RPG Moon Trek, small lunar navigation compass made of brushed silver regolith and teal glass, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/item-regolith-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "moon-trek-item-moonstone-lens": {
    contentHash:
      "d9cecec2d25e9f95f86dc94d81478b418190bd12cf163601e3e85461b2d70644",
    sourceType: "generated",
    prompt:
      "Moonstone Lens item icon for Fitness RPG Moon Trek, crescent-shaped polished moonstone lens with teal glow and silver rim, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/item-moonstone-lens.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "moon-trek-item-tycho-starblade": {
    contentHash:
      "082cfee42c634c2350bed1ce247c4990e8d6b7ce78002609b51e358af713cc1c",
    sourceType: "generated",
    prompt:
      "Tycho Starblade item icon for Fitness RPG Moon Trek, elegant short fantasy blade shaped like a bright crater ray with moon-silver metal and teal gemstone hilt, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/moon-trek/item-tycho-starblade.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "mars-traverse-hero": {
    contentHash:
      "a9430ba9817e92a3dfb355658e20be87503255ebee6a55b3a4f11ee4a426918a",
    sourceType: "generated",
    prompt:
      "Mars Traverse route hero image for Fitness RPG, symbolic expedition from Jezero Crater toward Olympus Mons, broad red Martian canyon rim, distant shield volcano, teal-aqua route beacons tracing a walking path, cinematic optimistic RPG adventure dashboard art, no text, no logos, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "mars-traverse-sight-jezero-delta": {
    contentHash:
      "260418d679696cfb33491d1d82a71e5429dc3d488bfd7c8a24a5c7af7f4cb9c6",
    sourceType: "generated",
    prompt:
      "Jezero Delta sight card for Fitness RPG Mars Traverse, dry present-day Mars landscape, Perseverance rover region inspiration, ancient river delta fan as exposed dusty sediment layers and eroded channel ridges, red-orange crater floor, rocky buttes, thin hazy Martian sky, teal sci-fi frame accents, cinematic game scene, readable at mobile size, absolutely no water, no lakes, no rivers, no wet reflections, no vegetation, no people, no text, no logos, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/sight-jezero-delta.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "mars-traverse-map-viking-mdim21": {
    contentHash:
      "387801cd66232a6fcbb1483ad090cdfb218558165b2cd5cc43bbd38daab97abc",
    sourceType: "public_domain_reference",
    sourceUrl:
      "https://commons.wikimedia.org/wiki/File:Mars_Viking_MDIM21_ClrMosaic_1km.jpg",
    publicDomainNote:
      "USGS/Wikimedia Mars Viking MDIM21 global color mosaic in simple cylindrical projection; public domain NASA/USGS planetary map data.",
    attributionRequired: false,
    prompt:
      "Fitness RPG Mars Traverse public-domain Mars map asset from the USGS/Wikimedia Mars Viking MDIM21 global color mosaic, used as route backdrop with waypoint overlays.",
    generationCommand:
      "curl -L https://commons.wikimedia.org/wiki/Special:Redirect/file/Mars_Viking_MDIM21_ClrMosaic_1km.jpg -o /tmp/mars-mdim21.jpg && node <sharp-script> /tmp/mars-mdim21.jpg public/assets/quests/mars-traverse/map-viking-mdim21-color-1km.webp",
    reviewStatus: "accepted",
  },
  "mars-traverse-badge-complete": {
    contentHash:
      "e8be6cc069c428f6260b72fd0f1c755b1d13d048dcf837e0851833b353f91fd7",
    sourceType: "generated",
    prompt:
      "Mars Traverse completion badge icon for a fantasy fitness RPG, circular red-planet medal with Olympus Mons caldera silhouette and small teal gemstone, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "mars-traverse-item-basalt-compass": {
    contentHash:
      "e44376c3d8dbbfcc295006606d00b0d6f196493441fb44243112bcc27048fb2c",
    sourceType: "generated",
    prompt:
      "Basalt Compass item icon for Fitness RPG Mars Traverse, rugged black Martian basalt compass with red dust in cracks and teal glass needle, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/item-basalt-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "mars-traverse-item-canyon-crystal": {
    contentHash:
      "e63151e1abd820887fee31c3f6153963c294f67b2d82553a72eaf73335a0dff7",
    sourceType: "generated",
    prompt:
      "Canyon Crystal item icon for Fitness RPG Mars Traverse, faceted red-orange crystal with bright teal core inspired by Valles Marineris canyon light, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/item-canyon-crystal.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "mars-traverse-item-volcano-sigil": {
    contentHash:
      "9b828e285bb0b5e3709cc589a2fac3345bd3c12b75a8e1b7f65100c71aa5e97e",
    sourceType: "generated",
    prompt:
      "Volcano Sigil item icon for Fitness RPG Mars Traverse, ornate red volcanic seal with Olympus Mons caldera motif and central teal gem, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/mars-traverse/item-volcano-sigil.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "four-moons-hero": {
    contentHash:
      "436638a27dd303aad490dbafe8102dc22ebc1f5509007e8ca9093e28fddeb888",
    sourceType: "generated",
    prompt:
      "Four Moons of Jupiter route hero image for Fitness RPG, staged grand tour across Io, Europa, Ganymede, and Callisto, Jupiter huge in the sky, four distinct moon landscapes connected by teal-aqua transfer gates, volcanic Io foreground blending into icy Europa and cratered Callisto, cinematic optimistic RPG adventure dashboard art, no text, no logos, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "four-moons-sight-io-jupiter-rise": {
    contentHash:
      "1b9487b14dd0cffbafed7d48488781afc5fbbddb61e2839553a8b36f7f719c36",
    sourceType: "generated",
    prompt:
      "Io Jupiter-Rise sight card for Fitness RPG Four Moons of Jupiter, bright volcanic plain on Io with colorful sulfur terrain, distant gentle volcanic plume, enormous Jupiter rising in black sky, teal sci-fi frame accents, cinematic game scene, readable at mobile size, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/sight-io-jupiter-rise.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "four-moons-badge-complete": {
    contentHash:
      "c9fc34f3f33ac9b93d0eb91db2e5bb425d0d5e166f1ef4da5cbec93db77a0cdd",
    sourceType: "generated",
    prompt:
      "Four Moons of Jupiter completion badge icon for a fantasy fitness RPG, circular medal showing four small Galilean moon gems orbiting a teal Jupiter emblem, volcanic red, icy blue, silver gray, and cratered bronze sections, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "four-moons-item-volcanic-compass": {
    contentHash:
      "cf1e1391598e22b569fd0193b4e9ffbf03e430190484495f1eba6dea1237c28b",
    sourceType: "generated",
    prompt:
      "Volcanic Compass item icon for Fitness RPG Four Moons of Jupiter, rugged compass forged from Io volcanic rock with glowing sulfur-orange rim and teal navigation needle, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/item-volcanic-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "four-moons-item-icebound-crystal": {
    contentHash:
      "00dca35433705810600e0e1107de6d1f7452553561d535ecccfa94988dd3e59d",
    sourceType: "generated",
    prompt:
      "Icebound Crystal item icon for Fitness RPG Four Moons of Jupiter, faceted icy Europa crystal with white-blue shell texture, subtle cracked ice bands, bright teal inner glow, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/item-icebound-crystal.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "four-moons-item-jovian-sigil": {
    contentHash:
      "2070f2f481677f76e08bc9e5b4c434df7b39f1ab21752b84da2bab3166fb1d68",
    sourceType: "generated",
    prompt:
      "Jovian Sigil item icon for Fitness RPG Four Moons of Jupiter, ornate circular sigil with Jupiter storm bands, four tiny moon stones around the edge, teal center gem, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/four-moons/item-jovian-sigil.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "appalachian-trail-hero": {
    contentHash:
      "cac5673c8e2e3d7606b3d9ba30fb8996516ac3ecc122d0b87c14c15da6b2d8a6",
    sourceType: "generated",
    prompt:
      "Appalachian Trail route hero image for Fitness RPG, northbound thru-hike from Springer Mountain to Katahdin, lush green ridge trail with white blazes, layered Blue Ridge and New England mountain silhouettes, teal-aqua quest route glow woven subtly along the footpath, optimistic RPG adventure dashboard art, no text, no logos, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "appalachian-trail-sight-springer-mountain": {
    contentHash:
      "3c14092c0f057a022add72057f62a0e881170a1dbdc989be0c4e16e16e6466cd",
    sourceType: "generated",
    prompt:
      "Springer Mountain sight card for Fitness RPG Appalachian Trail, peaceful southern terminus trail scene with forested ridge, white blaze on a post, early morning light, teal UI accent glow, cinematic game scene, readable at mobile size, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/sight-springer-mountain.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "appalachian-trail-badge-complete": {
    contentHash:
      "d85fd393d8e6d6e400c2084ae6fad4c12bf4778a276ceb21be3c6c18a5d6b2af",
    sourceType: "generated",
    prompt:
      "Appalachian Trail completion badge icon for a fantasy fitness RPG, circular green mountain medal with white blaze motif, Katahdin summit silhouette, small teal gemstone, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "appalachian-trail-item-white-blaze-compass": {
    contentHash:
      "dc5bac0cbcafeb9dd4150c6287d69ab36519574e45f89f289b03ba5018ef665d",
    sourceType: "generated",
    prompt:
      "White Blaze Compass item icon for Fitness RPG Appalachian Trail, rugged hiking compass with a white blaze marker, green enamel, worn brass, teal needle glow, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/item-white-blaze-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "appalachian-trail-item-ridge-crystal": {
    contentHash:
      "681256c37413f13f1efa66d7ebabc4d28c2e87d6c900dc29a0cb5ba3fed2dbe8",
    sourceType: "generated",
    prompt:
      "Ridge Crystal item icon for Fitness RPG Appalachian Trail, faceted green mountain crystal with layered ridge lines inside and subtle teal glow, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/item-ridge-crystal.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "appalachian-trail-item-summit-staff": {
    contentHash:
      "3fe00a20d046e36b77de3cdc38fbf71297e1cec2fc6e4a47d9c818c812b3a8ff",
    sourceType: "generated",
    prompt:
      "Summit Staff item icon for Fitness RPG Appalachian Trail, carved wooden hiking staff with mountain-summit silhouette charm, white blaze ribbon, green leaves, small teal crystal set into the top, centered RPG inventory item, strong silhouette, clean transparent background, no sign, no plaque, no letters, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/appalachian-trail/item-summit-staff.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-hero": {
    contentHash:
      "a421dabac80a7f25b1f7fce295b65626357e9868570d73f75dd0ef23bfc855f0",
    sourceType: "generated",
    prompt:
      "Pacific Crest Trail route hero image for Fitness RPG, northbound thru-hike from the southern monument near Campo to the Canadian border, desert sunrise transitioning into Sierra granite and Cascade volcanic ridges, teal-aqua quest route glow along the trail, optimistic RPG adventure dashboard art, no text, no logos, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-sight-southern-terminus": {
    contentHash:
      "25f8d81956ce10e632f74698862f91cf30b105651b111935d9dcdc932c853ad9",
    sourceType: "generated",
    prompt:
      "Southern Terminus sight card for Fitness RPG Pacific Crest Trail, desert trail near Campo with monument-like trail marker silhouette, golden sunrise, chaparral and distant mountains, teal UI accent glow, cinematic game scene, readable at mobile size, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/sight-southern-terminus.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-badge-complete": {
    contentHash:
      "cdfb93ced73e63c4de7dbdbf39309a850a096c2bf308527ba4ad47deff31ecf5",
    sourceType: "generated",
    prompt:
      "Pacific Crest Trail completion badge icon for a fantasy fitness RPG, circular trail medal with desert sunrise, Sierra granite, Cascade volcano silhouette, and small teal gemstone, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-item-desert-compass": {
    contentHash:
      "23b78d6701ff53e9981e32752e05516f575dbea97f0c9c39ea84d64fb2d4a6ad",
    sourceType: "generated",
    prompt:
      "Desert Compass item icon for Fitness RPG Pacific Crest Trail, rugged brass hiking compass with desert stone rim, cactus-flower detail, teal navigation needle glow, abstract tick marks only, centered RPG inventory item, strong silhouette, clean transparent background, no cardinal letters, no words, no symbols, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/item-desert-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-item-cascade-crystal": {
    contentHash:
      "b774435e7a82558916915b9711e7f90485da53c193f3cb92828ba66e51f58a20",
    sourceType: "generated",
    prompt:
      "Cascade Crystal item icon for Fitness RPG Pacific Crest Trail, faceted mountain crystal with snowy Cascade ridge lines and evergreen shapes inside, teal inner glow, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/item-cascade-crystal.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "pacific-crest-trail-item-border-lantern": {
    contentHash:
      "1c5cd67df06bd326937915c43142b5b8643de1a9878ffe0cce5eecc39130cb95",
    sourceType: "generated",
    prompt:
      "Border Lantern item icon for Fitness RPG Pacific Crest Trail, small trail lantern shaped like a border monument beacon, brushed steel and warm gold light with teal crystal flame, evergreen and mountain details, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/pacific-crest-trail/item-border-lantern.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "national-parks-grand-tour-hero": {
    contentHash:
      "a99927599b100a162ecab57600ee3c367f8ee61240fb96f804e0d1d52306429c",
    sourceType: "generated",
    prompt:
      "National Parks Grand Tour route hero image for Fitness RPG, walking-powered journey through diverse U.S. national park landscapes, red desert canyon, alpine lake, giant forest, coastal cliffs, snowy mountain, and teal-aqua route path connecting park markers, optimistic RPG adventure dashboard art, no text, no logos, no official emblems, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/national-parks-grand-tour/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "national-parks-grand-tour-sight-acadia": {
    contentHash:
      "3651750bedb24ed24bea2921314da65c465eb5d005fb103f0b028596cdb624ea",
    sourceType: "generated",
    prompt:
      "Acadia sight card for Fitness RPG National Parks Grand Tour, rocky Atlantic coast, pine trees, sunrise over ocean, granite trail overlook, subtle teal UI accent glow, cinematic game scene, readable at mobile size, no text, no logos, no official emblems, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/national-parks-grand-tour/sight-acadia.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "national-parks-grand-tour-item-park-chest": {
    contentHash:
      "bd367aaf2a0c47e2d1936cdc7c146fda5598a68eb328e8c1891bddcf0c84fb55",
    sourceType: "generated",
    prompt:
      "Park Chest item icon for Fitness RPG National Parks Grand Tour, small wooden treasure chest with trail-map texture, pine branch, canyon stone, ocean shell, and teal crystal clasp, centered RPG inventory item, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/national-parks-grand-tour/item-park-chest.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "national-parks-grand-tour-badge-regional-parks": {
    contentHash:
      "6b52a549434abf6cd8dce8670d33489a0f2528d93a570099e1e239495be38195",
    sourceType: "generated",
    prompt:
      "Regional Parks Badge item icon for Fitness RPG National Parks Grand Tour, shield-shaped regional adventure badge with abstract compass, forest, canyon, glacier, and coast motifs, teal center gem, centered, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/national-parks-grand-tour/badge-regional-parks.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "national-parks-grand-tour-badge-grand-passport": {
    contentHash:
      "86484ef254ff0a29a60ca5453a681ce00a99244a42a4e883934ebd9756521be8",
    sourceType: "generated",
    prompt:
      "National Parks Grand Tour completion badge icon for a fantasy fitness RPG, circular passport-style medal with abstract mountain, canyon, forest, ocean, and star symbols, teal gemstone, centered, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/national-parks-grand-tour/badge-grand-passport.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "earth-city-tour-hero": {
    contentHash:
      "ffb6d8be221035e41a2f41cf3ba74d6268c107df6ce6108ee8a7e3fe70daa05c",
    sourceType: "generated",
    prompt:
      "Earth City Tour route hero image for Fitness RPG, walking-powered curated U.S. city waypoint quest, stylized map-like route connecting diverse city skylines from coast to mountains to plains, teal-aqua path with waypoint pins, optimistic RPG adventure dashboard art, no text, no logos, no official emblems, no watermark, no real people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/earth-city-tour/hero.png --size 1536x864 --quality high',
    reviewStatus: "accepted",
  },
  "earth-city-tour-sight-birmingham": {
    contentHash:
      "33857ecb8d8175ff013f2ff8d82ea567cfc32572410f026aa593f9369556edae",
    sourceType: "generated",
    prompt:
      "Birmingham city stop sight card for Fitness RPG Earth City Tour, warm southern city skyline at dusk with tree-lined streets and distant hills, subtle teal UI accent glow, cinematic game scene, readable at mobile size, no text, no logos, no official emblems, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/earth-city-tour/sight-birmingham.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "earth-city-tour-item-city-chest": {
    contentHash:
      "92e60a6464c333eb0448995f76ed42ba1e335b08d5a3081c35582b32bd4b9e5b",
    sourceType: "generated",
    prompt:
      "City Chest item icon for Fitness RPG Earth City Tour, small treasure chest made of street-map panels and brass corners, skyline charm, teal crystal lock, centered RPG inventory item, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/earth-city-tour/item-city-chest.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "earth-city-tour-badge-regional-cities": {
    contentHash:
      "63ec1d22b93952934f71accf9fc36ae1d3171bce121be3caf703f6fbe7f2f78f",
    sourceType: "generated",
    prompt:
      "Regional Cities Badge icon for Fitness RPG Earth City Tour, shield-shaped badge with abstract city skyline, compass route arcs, mountains and coast motifs, teal center gem, centered, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/earth-city-tour/badge-regional-cities.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "earth-city-tour-badge-complete": {
    contentHash:
      "f06510c159a4dcb6c2770c0dd0ea07e1ac665b8a00b58c0783e365268eacc800",
    sourceType: "generated",
    prompt:
      "Earth City Tour completion badge icon for a fantasy fitness RPG, circular city-map medal with skyline silhouettes, road-route arcs, teal gemstone waypoint, centered, strong silhouette, clean transparent background, no text, no letters, no logos, no official emblems, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/earth-city-tour/badge-complete.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "under-the-sea-hero": {
    contentHash:
      "c52c224a413fe3ed9d5af952e3c2b524e461111461976b898f48266cc0f6194c",
    sourceType: "generated",
    prompt:
      "Under the Sea route hero for Fitness RPG, Blue Atlas Expedition, cheerful research submersible over coral reef fading into deep blue ocean floor map contours, sea turtles, small reef fish, kelp silhouettes, distant whale shape, teal-aqua glowing route path, polished cinematic game dashboard art, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/hero.png --size 1536x864 --quality medium',
    reviewStatus: "accepted",
  },
  "under-the-sea-map-noaa-etopo": {
    contentHash:
      "5cc0de6cbb643e14ad9818bfd25aafeab5379166188a997c69c49ab2a62fbe62",
    sourceType: "public_domain_reference",
    sourceUrl:
      "https://commons.wikimedia.org/wiki/File:NOAA_ETOPO_Global_Relief_map.jpg",
    publicDomainNote:
      "NOAA ETOPO global relief image derived from U.S. National Oceanic and Atmospheric Administration material and marked public domain on Wikimedia Commons. NOAA ETOPO integrates global topography, bathymetry, and shoreline data.",
    attributionRequired: false,
    prompt:
      "Fitness RPG Under the Sea public-domain NOAA ETOPO global relief map asset, used as a bathymetry/topography route backdrop with waypoint overlays.",
    generationCommand:
      "curl -L https://upload.wikimedia.org/wikipedia/commons/7/78/NOAA_ETOPO_Global_Relief_map.jpg -o public/assets/quests/under-the-sea/map-noaa-etopo-global-relief.jpg && node <sharp-script> map-noaa-etopo-global-relief.webp",
    reviewStatus: "accepted",
  },
  "under-the-sea-sight-florida-keys": {
    contentHash:
      "261a28c3692a04c1924fd4c8ad06935cc632b7b9b9bd1364265f7ab0b8ae1571",
    sourceType: "generated",
    prompt:
      "Florida Keys reef sight card for Fitness RPG Under the Sea, sunlit coral reef and seagrass meadow, parrotfish, sea turtle, tiny reef fish, clear turquoise water, gentle teal UI accent glow, cheerful underwater adventure scene, readable at mobile size, no text, no logos, no watermark, no people",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/sight-florida-keys.png --size 1280x720 --quality medium',
    reviewStatus: "accepted",
  },
  "under-the-sea-item-conch-compass": {
    contentHash:
      "7e990770454fd940a83e2ffeece898b43e86eb93b58f7bb048b5c782fd78eafa",
    sourceType: "generated",
    prompt:
      "Conch Compass item icon for Fitness RPG Under the Sea, polished conch shell merged with brass compass and teal glass needle, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/item-conch-compass.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "under-the-sea-item-chemosynthesis-rune": {
    contentHash:
      "0b677fb58c3a4f0b21f344b9e789c8063ba55ef820933b7b4e60670289915325",
    sourceType: "generated",
    prompt:
      "Chemosynthesis Rune item icon for Fitness RPG Under the Sea, deep sea hydrothermal vent rock cradle with glowing teal biological rune and small red tube worm details, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/item-chemosynthesis-rune.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "under-the-sea-item-abyssal-pressure-core": {
    contentHash:
      "9d1b86e2d9f9a3c72a15d629bcbbdd53efaff297bdc96203acd5fcc7f9a10a86",
    sourceType: "generated",
    prompt:
      "Abyssal Pressure Core item icon for Fitness RPG Under the Sea, deep blue glowing orb held in dark pressure-ring metal bands with tiny bubbles, centered RPG inventory item, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/item-abyssal-pressure-core.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
  "under-the-sea-badge-blue-atlas": {
    contentHash:
      "4a6eb1144c2bb3345a364abf916536871b6522926b32d3a9ec6417f5ecfad433",
    sourceType: "generated",
    prompt:
      "Blue Atlas Expedition completion badge icon for Fitness RPG Under the Sea, circular ocean medal with whale tail, coral branch, kelp, shell wave bands, and bright teal pearl center, centered, strong silhouette, clean transparent background, no text, no logo, no watermark",
    generationCommand:
      'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output public/assets/quests/under-the-sea/badge-blue-atlas.png --size 1024x1024 --quality medium --background transparent --transparent-mode fallback-model',
    reviewStatus: "accepted",
  },
};

export const questAssetManifest: QuestAssetManifestEntry[] = routeCatalog.flatMap(
  (route) =>
    route.assets.map((asset) => {
      const generated = generatedAssetOverrides[asset.assetId];
      return {
        assetId: asset.assetId,
        routeId: route.routeId,
        kind: asset.kind,
        path: asset.path,
        optimizedPath: optimizedPathFor(asset.path),
        contentHash: null,
        sourceType: "generated_placeholder",
        sourceUrl: null,
        publicDomainNote:
          "Placeholder manifest entry. Replace with generated or public-domain sourced asset before marking route assets complete.",
        attributionRequired: false,
        prompt: heroPromptForRoute(route.title, route.description),
        generationCommand:
          'node .codex/skills/imagegen2/generate.cjs --prompt "<prompt>" --output "public/assets/quests/<route>/<asset>.png" --size 1536x864 --quality high',
        reviewStatus: "planned",
        blockedMarks: [],
        ...generated,
      } satisfies QuestAssetManifestEntry;
    }),
);

export function getQuestAsset(assetId: string): QuestAssetManifestEntry | null {
  return questAssetManifest.find((asset) => asset.assetId === assetId) ?? null;
}

export function displayQuestAssetPath(assetId: string): string | null {
  const asset = getQuestAsset(assetId);
  if (!asset) return null;
  return asset.reviewStatus === "accepted" ? asset.optimizedPath : asset.path;
}

function optimizedPathFor(path: string): string {
  return path.replace(/\.(png|jpe?g)$/iu, ".webp");
}

function heroPromptForRoute(title: string, description: string): string {
  return [
    `${title} route hero image for Fitness RPG.`,
    description,
    "Cinematic but readable mobile-first game dashboard art, no logos, no text, no real people, optimistic adventure tone.",
  ].join(" ");
}
